import { supabase } from './supabase'

const DEFAULT_EXPIRY = 3600 // 1 hour in seconds

type BucketName =
  | 'issue-photos'
  | 'issue-documents'
  | 'property-photos'
  | 'compliance-documents'
  | 'expense-receipts'
  | 'tenant-documents'
  | 'property-manuals'
  | 'right-to-rent'
  | 'agreements'
  | 'remittances'

/**
 * Generate a signed URL for a storage object (1-hour expiry by default).
 * Returns empty string if path is falsy or signing fails.
 */
export async function getSignedUrl(
  bucket: BucketName,
  path: string,
  expiresIn: number = DEFAULT_EXPIRY
): Promise<string> {
  if (!path) return ''

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn)

  if (error || !data?.signedUrl) return ''
  return data.signedUrl
}

/**
 * Generate signed URLs for multiple storage objects in a single batch call.
 * Returns array of URLs in the same order as the input paths.
 * Failed entries return empty string.
 */
export async function getSignedUrls(
  bucket: BucketName,
  paths: string[],
  expiresIn: number = DEFAULT_EXPIRY
): Promise<string[]> {
  if (paths.length === 0) return []

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrls(paths, expiresIn)

  if (error || !data) return paths.map(() => '')

  // createSignedUrls returns results in the same order as input paths
  return data.map(item => item.signedUrl || '')
}

/**
 * Extract the storage path from a value that may be either:
 * - A bare storage path (new format): "property-id/doc-type/filename.pdf"
 * - A full public URL (legacy format): "https://...supabase.co/storage/v1/object/public/bucket-name/path"
 *
 * This handles backward compatibility for DB columns that previously stored full public URLs.
 */
export function extractStoragePath(bucket: BucketName, urlOrPath: string): string {
  if (!urlOrPath) return ''

  // Already a bare path — not a URL
  if (!urlOrPath.startsWith('http')) return urlOrPath

  // Extract path from full public URL
  // Format: .../storage/v1/object/public/{bucket}/{path}
  const marker = `/object/public/${bucket}/`
  const idx = urlOrPath.indexOf(marker)
  if (idx !== -1) {
    return decodeURIComponent(urlOrPath.slice(idx + marker.length))
  }

  // Fallback: try signed URL format
  // Format: .../storage/v1/object/sign/{bucket}/{path}?token=...
  const signMarker = `/object/sign/${bucket}/`
  const signIdx = urlOrPath.indexOf(signMarker)
  if (signIdx !== -1) {
    const pathWithQuery = urlOrPath.slice(signIdx + signMarker.length)
    const queryIdx = pathWithQuery.indexOf('?')
    return decodeURIComponent(queryIdx !== -1 ? pathWithQuery.slice(0, queryIdx) : pathWithQuery)
  }

  // Could not parse — return as-is and let the signed URL call handle the error
  return urlOrPath
}

/**
 * Log access to Right to Rent documents (UK GDPR Article 9 — special category data).
 * Non-blocking — failures are silently logged.
 */
export async function logRightToRentAccess(
  documentId: string,
  accessType: 'view' | 'download' | 'upload' | 'delete'
): Promise<void> {
  try {
    const session = (await supabase.auth.getSession()).data.session
    if (!session?.user?.id) return

    await supabase.from('right_to_rent_access_log').insert({
      document_id: documentId,
      accessed_by: session.user.id,
      access_type: accessType,
      user_agent: navigator.userAgent,
    })
  } catch {
    // Non-blocking — access logging should never break the user flow
  }
}
